import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Building2, Users, FileText, Key, BarChart3, Settings, Plus, 
  Trash2, Edit, Copy, RefreshCw, Download, LogOut, Shield,
  CheckCircle, XCircle, Clock, AlertTriangle, Upload, Activity, Box,
  Target, Image as ImageIcon
} from "lucide-react";
import { BrandedFooter } from "@/components/BrandedFooter";
import ZoneMarker from "@/components/ZoneMarker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_docuflow-68/artifacts/y7jajers_Well%20Connected%20Logo_Feb2024_600px.jpg";

export default function AdminPortal() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [user, setUser] = useState(null);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [processingStats, setProcessingStats] = useState(null);
  const [processingLogs, setProcessingLogs] = useState([]);
  const [agentExeStatus, setAgentExeStatus] = useState(null);
  const [refreshingExe, setRefreshingExe] = useState(false);
  const [githubConfig, setGithubConfig] = useState(null);
  const [newGithubToken, setNewGithubToken] = useState("");
  const [savingGithubToken, setSavingGithubToken] = useState(false);
  const [uploadingExe, setUploadingExe] = useState(false);
  const [statsFilter, setStatsFilter] = useState("all"); // all, today, 7days, 30days, month, custom
  const [statsCustomDate, setStatsCustomDate] = useState("");
  const [statsCustomMonth, setStatsCustomMonth] = useState("");
  
  // Dialog states
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  
  // Form states
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      if (parsed.role !== "admin") {
        navigate("/dashboard");
        return;
      }
      setUser(parsed);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchStats();
      fetchApplications();
      fetchCompanies();
      fetchTemplates();
      fetchLicenses();
      fetchLogs();
      fetchUsers();
      fetchProcessingStats();
      fetchProcessingLogs();
      fetchAgentExeStatus();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchProcessingStats = async (filter = statsFilter, customDate = statsCustomDate, customMonth = statsCustomMonth) => {
    try {
      let url = `${API}/admin/processing-stats`;
      const params = new URLSearchParams();
      
      if (filter === "today") {
        const today = new Date().toISOString().split('T')[0];
        params.append("date", today);
      } else if (filter === "7days") {
        params.append("days", "7");
      } else if (filter === "30days") {
        params.append("days", "30");
      } else if (filter === "month" && customMonth) {
        params.append("month", customMonth);
      } else if (filter === "date" && customDate) {
        params.append("date", customDate);
      }
      // "all" = no params = all time
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setProcessingStats(response.data);
    } catch (error) {
      console.error("Failed to fetch processing stats:", error);
    }
  };

  // Refetch stats when filter changes
  useEffect(() => {
    if (user?.role === "admin") {
      fetchProcessingStats(statsFilter, statsCustomDate, statsCustomMonth);
    }
  }, [statsFilter, statsCustomDate, statsCustomMonth]);

  const fetchProcessingLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/processing-logs`, { headers: getAuthHeaders() });
      setProcessingLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch processing logs:", error);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/applications`, { headers: getAuthHeaders() });
      setApplications(response.data);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies`, { headers: getAuthHeaders() });
      setCompanies(response.data);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/templates`, { headers: getAuthHeaders() });
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const fetchLicenses = async () => {
    try {
      const response = await axios.get(`${API}/admin/licenses`, { headers: getAuthHeaders() });
      setLicenses(response.data);
    } catch (error) {
      console.error("Failed to fetch licenses:", error);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/logs?limit=50`, { headers: getAuthHeaders() });
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchAgentExeStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/agent-exe-status`, { headers: getAuthHeaders() });
      setAgentExeStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch agent exe status:", error);
    }
    fetchGithubConfig();
  };

  const fetchGithubConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/github-config`, { headers: getAuthHeaders() });
      setGithubConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch github config:", error);
    }
  };

  const handleSaveGithubToken = async () => {
    try {
      setSavingGithubToken(true);
      const res = await axios.post(`${API}/admin/github-config`, { token: newGithubToken.trim() }, { headers: getAuthHeaders() });
      toast.success(`${res.data.message}${res.data.latest_release ? ` — latest release: ${res.data.latest_release}` : ""}`);
      setNewGithubToken("");
      fetchGithubConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save token");
    } finally {
      setSavingGithubToken(false);
    }
  };

  const handleExeUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.exe')) {
      toast.error("Please select an .exe file");
      return;
    }
    
    setUploadingExe(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/admin/upload-agent-exe`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Agent exe uploaded successfully!");
      fetchAgentExeStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload exe");
    } finally {
      setUploadingExe(false);
    }
  };

  const handleDeleteExe = async () => {
    if (!confirm("Delete the agent exe? Customers will get Python version until you upload a new one.")) return;
    
    try {
      await axios.delete(`${API}/admin/agent-exe`, { headers: getAuthHeaders() });
      toast.success("Agent exe deleted");
      fetchAgentExeStatus();
    } catch (error) {
      toast.error("Failed to delete exe");
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend to clear server-side session and cookies
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.log("Logout API error (non-critical):", error);
    }
    // Clear local storage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  // Company CRUD
  const handleSaveCompany = async (formData) => {
    try {
      if (editingCompany) {
        await axios.put(`${API}/admin/companies/${editingCompany.company_id}`, formData, { headers: getAuthHeaders() });
        toast.success("Company updated");
      } else {
        await axios.post(`${API}/admin/companies`, formData, { headers: getAuthHeaders() });
        toast.success("Company created");
      }
      setCompanyDialogOpen(false);
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save company");
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm("Delete this company and all associated data?")) return;
    try {
      await axios.delete(`${API}/admin/companies/${companyId}`, { headers: getAuthHeaders() });
      toast.success("Company deleted");
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to delete company");
    }
  };

  // License CRUD
  const handleCreateLicense = async (companyId, appId, expiryDays, maxDevices) => {
    try {
      const response = await axios.post(`${API}/admin/licenses`, {
        company_id: companyId,
        app_id: appId,
        expiry_days: expiryDays,
        max_devices: maxDevices
      }, { headers: getAuthHeaders() });
      toast.success("License created");
      setLicenseDialogOpen(false);
      fetchLicenses();
      fetchCompanies();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create license");
    }
  };

  const handleRevokeLicense = async (licenseId) => {
    if (!window.confirm("Revoke this license?")) return;
    try {
      await axios.post(`${API}/admin/licenses/${licenseId}/revoke`, {}, { headers: getAuthHeaders() });
      toast.success("License revoked");
      fetchLicenses();
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to revoke license");
    }
  };

  // Template CRUD
  const handleSaveTemplate = async (formData) => {
    try {
      if (editingTemplate) {
        await axios.put(`${API}/admin/templates/${editingTemplate.template_id}`, formData, { headers: getAuthHeaders() });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/admin/templates`, formData, { headers: getAuthHeaders() });
        toast.success("Template created");
      }
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error("Template save error:", error.response?.data);
      
      // Handle specific error types with better messages
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 403) {
        // Check if it's actually an auth issue or something else
        if (detail === "Admin access required") {
          toast.error("Session expired. Please refresh the page and try again.");
        } else {
          toast.error(detail || "Access denied");
        }
      } else if (status === 422) {
        // Validation error - parse the detail
        if (Array.isArray(detail)) {
          const fieldErrors = detail.map(e => {
            const field = e.loc?.slice(-1)[0] || "field";
            return `${field}: ${e.msg}`;
          }).join(", ");
          toast.error(`Validation error: ${fieldErrors}`);
        } else {
          toast.error(detail || "Please check all required fields are filled");
        }
      } else if (status === 404) {
        toast.error(detail || "Company not found. Please select a valid company.");
      } else {
        toast.error(detail || "Failed to save template. Please try again.");
      }
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/admin/templates/${templateId}`, { headers: getAuthHeaders() });
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const exportLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/logs/export`, {
        headers: getAuthHeaders(),
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `logs_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      toast.success("Logs exported");
    } catch (error) {
      toast.error("Failed to export logs");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b bg-card px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="WELL Connected" className="brand-logo" />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user.email}</span>
            <Badge className="bg-[#E01626] text-white">Admin</Badge>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 flex-1 w-full">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-8 flex-wrap">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <BarChart3 className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">
              <Box className="h-4 w-4 mr-2" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="companies" data-testid="tab-companies">
              <Building2 className="h-4 w-4 mr-2" />
              Companies
            </TabsTrigger>
            <TabsTrigger value="licenses" data-testid="tab-licenses">
              <Key className="h-4 w-4 mr-2" />
              Licenses
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">
              <FileText className="h-4 w-4 mr-2" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="processing" data-testid="tab-processing">
              <Activity className="h-4 w-4 mr-2" />
              Processing Stats
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="agent" data-testid="tab-agent">
              <Download className="h-4 w-4 mr-2" />
              Agent
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="w-full">
            <div className="space-y-6">
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card data-testid="stat-companies">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Companies</p>
                          <p className="text-3xl font-heading font-semibold">{stats.companies.active}</p>
                          <p className="text-xs text-muted-foreground">{stats.companies.total} total</p>
                        </div>
                        <Building2 className="h-8 w-8 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card data-testid="stat-templates">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Templates</p>
                          <p className="text-3xl font-heading font-semibold">{stats.templates}</p>
                        </div>
                        <FileText className="h-8 w-8 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card data-testid="stat-processed">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Processed Today</p>
                          <p className="text-3xl font-heading font-semibold text-green-600">{stats.processing.today}</p>
                          <p className="text-xs text-muted-foreground">{stats.processing.total} total</p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card data-testid="stat-success-rate">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Success Rate</p>
                          <p className="text-3xl font-heading font-semibold">{stats.processing.success_rate}%</p>
                          <p className="text-xs text-muted-foreground">{stats.processing.failed} failed</p>
                        </div>
                        <BarChart3 className="h-8 w-8 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
              </div>
            )}

            {/* Today's Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Today's Activity Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.processing?.today > 0 ? (
                  <div className="text-center py-4">
                    <p className="text-4xl font-bold text-[#1a3a6e]">{stats.processing.today}</p>
                    <p className="text-muted-foreground">documents processed today</p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No documents processed today. View detailed stats in the Processing Stats tab.
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Processing Stats Tab */}
          <TabsContent value="processing" className="w-full">
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-heading font-semibold">Processing Statistics</h2>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Date Filter */}
                  <Select value={statsFilter} onValueChange={(v) => {
                    setStatsFilter(v);
                    if (v !== "month") setStatsCustomMonth("");
                    if (v !== "date") setStatsCustomDate("");
                  }}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Filter by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="7days">Last 7 Days</SelectItem>
                      <SelectItem value="30days">Last 30 Days</SelectItem>
                      <SelectItem value="month">Select Month</SelectItem>
                      <SelectItem value="date">Select Date</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Month Picker */}
                  {statsFilter === "month" && (
                    <input
                      type="month"
                      value={statsCustomMonth}
                      onChange={(e) => setStatsCustomMonth(e.target.value)}
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                  )}
                  
                  {/* Date Picker */}
                  {statsFilter === "date" && (
                    <input
                      type="date"
                      value={statsCustomDate}
                      onChange={(e) => setStatsCustomDate(e.target.value)}
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                  )}
                  
                  <Button variant="outline" size="sm" onClick={async () => { 
                    await fetchProcessingStats(); 
                    toast.success("Stats refreshed");
                  }}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Period Label */}
              {processingStats?.period_label && (
                <div className="text-sm text-muted-foreground">
                  Showing data for: <span className="font-medium text-foreground">{processingStats.period_label}</span>
                </div>
              )}

              {/* Overall Stats */}
              {processingStats?.overall && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="bg-[#1a3a6e] text-white">
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold">{processingStats.overall.total_scans}</p>
                      <p className="text-sm opacity-80">Total Scans</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-600 text-white">
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold">{processingStats.overall.successful_scans}</p>
                      <p className="text-sm opacity-80">Successful</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-600 text-white">
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold">{processingStats.overall.failed_scans}</p>
                      <p className="text-sm opacity-80">Failed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-600 text-white">
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold">{processingStats.overall.total_pages || 0}</p>
                      <p className="text-sm opacity-80">Pages Processed</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold text-[#1a3a6e]">{processingStats.overall.success_rate}%</p>
                      <p className="text-sm text-muted-foreground">Success Rate</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Stats by Company */}
              <Card>
                <CardHeader>
                  <CardTitle>Scans by Company</CardTitle>
                  <CardDescription>Document processing counts per company</CardDescription>
                </CardHeader>
                <CardContent>
                  {processingStats?.by_company?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Company</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Total Scans</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Pages</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Successful</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Failed</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Success Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processingStats.by_company.map((stat, idx) => (
                            <tr key={idx} className="border-b hover:bg-muted/50">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">{stat.company_name}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right font-mono font-semibold">{stat.total_scans}</td>
                              <td className="py-3 px-4 text-right font-mono text-purple-600">{stat.total_pages || stat.total_scans}</td>
                              <td className="py-3 px-4 text-right font-mono text-green-600">{stat.successful_scans}</td>
                              <td className="py-3 px-4 text-right font-mono text-red-600">{stat.failed_scans}</td>
                              <td className="py-3 px-4 text-right">
                                <Badge className={stat.success_rate >= 90 ? "bg-green-100 text-green-700" : stat.success_rate >= 70 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}>
                                  {stat.success_rate}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No processing data yet. Once clients start using the desktop agent, their scan counts will appear here.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications" className="w-full">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-heading font-semibold">Applications</h2>
              </div>

              <div className="grid gap-4">
              {applications.map((app) => (
                <Card key={app.app_id} className="overflow-hidden">
                  <div className="h-2" style={{ backgroundColor: app.color }} />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: app.color + "15" }}
                        >
                          <Box className="h-6 w-6" style={{ color: app.color }} />
                        </div>
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{app.name}</h3>
                            <Badge variant={app.status === "active" ? "default" : "secondary"}>
                              {app.status === "active" ? "Active" : app.status === "coming_soon" ? "Coming Soon" : app.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{app.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>{licenses.filter(l => l.app_id === app.app_id).length} license(s)</span>
                            <span className="font-mono">{app.app_id}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>

            {applications.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Box className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No applications configured</p>
              </div>
            )}
            </div>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="w-full">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-heading font-semibold">Companies</h2>
                <Button onClick={() => { setEditingCompany(null); setCompanyDialogOpen(true); }} data-testid="add-company-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Company
                </Button>
              </div>

              <div className="grid gap-4">
              {companies.map((company) => (
                <Card key={company.company_id} data-testid={`company-${company.company_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-heading text-lg font-semibold">{company.name}</h3>
                          <Badge variant={company.is_active ? "default" : "secondary"}>
                            {company.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{company.contact_email}</p>
                        <div className="flex items-center gap-4 mt-3 text-sm flex-wrap">
                          <span>OCR: <Badge variant="outline">{company.ocr_engine}</Badge></span>
                          <span>Devices: {company.active_devices}/{company.max_devices}</span>
                          {company.license_key && (
                            <span className="flex items-center gap-1">
                              License: 
                              <code className="bg-muted px-2 py-0.5 rounded text-xs">{company.license_key}</code>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(company.license_key)}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </span>
                          )}
                        </div>
                        {/* Page Usage Section */}
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Page Usage</span>
                            <span className="text-sm">
                              <span className={company.pages_used >= company.page_limit ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                                {company.pages_used || 0}
                              </span>
                              <span className="text-muted-foreground"> / {company.page_limit || 1000} pages</span>
                            </span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${
                                (company.pages_used || 0) >= (company.page_limit || 1000) 
                                  ? 'bg-red-500' 
                                  : (company.pages_used || 0) >= (company.page_limit || 1000) * 0.8 
                                    ? 'bg-yellow-500' 
                                    : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(((company.pages_used || 0) / (company.page_limit || 1000)) * 100, 100)}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-muted-foreground">
                              {Math.round(((company.pages_used || 0) / (company.page_limit || 1000)) * 100)}% used
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/admin/companies/${company.company_id}/reset-usage`, {}, { headers: getAuthHeaders() });
                                  fetchCompanies();
                                  toast.success("Page usage reset");
                                } catch (err) {
                                  toast.error("Failed to reset usage");
                                }
                              }}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Reset
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => { setEditingCompany(company); setCompanyDialogOpen(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDeleteCompany(company.company_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Company Dialog */}
            <CompanyDialog 
              open={companyDialogOpen} 
              onOpenChange={setCompanyDialogOpen}
              company={editingCompany}
              onSave={handleSaveCompany}
            />
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="w-full">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-heading font-semibold">OCR Templates</h2>
                <Button onClick={() => { setEditingTemplate(null); setTemplateDialogOpen(true); }} data-testid="add-template-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Template
                </Button>
              </div>

              <div className="grid gap-4">
              {templates.map((template) => (
                <Card key={template.template_id} data-testid={`template-${template.template_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-heading text-lg font-semibold">{template.name}</h3>
                          <Badge variant="outline">{template.document_type}</Badge>
                          <Badge variant={template.is_active ? "default" : "secondary"}>
                            {template.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Company: {companies.find(c => c.company_id === template.company_id)?.name || template.company_id}
                        </p>
                        <div className="mt-3">
                          <p className="text-sm"><strong>Fields:</strong> {template.fields.map(f => f.field_name).join(", ")}</p>
                          <p className="text-sm mt-1"><strong>Filename Format:</strong> <code className="bg-muted px-2 py-0.5 rounded">{template.filename_format}</code></p>
                          <p className="text-sm mt-1"><strong>Page Scanning:</strong>{" "}
                            <Badge variant="outline" className="ml-1">
                              {template.page_scan_mode === "first_only" && "First page only"}
                              {template.page_scan_mode === "first_n" && `First ${template.page_scan_value} pages`}
                              {template.page_scan_mode === "specific" && `Pages ${template.page_scan_value}`}
                              {template.page_scan_mode === "range" && `Pages ${template.page_scan_value}`}
                            </Badge>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingTemplate(template); setTemplateDialogOpen(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDeleteTemplate(template.template_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Template Dialog */}
            <TemplateDialog 
              open={templateDialogOpen} 
              onOpenChange={setTemplateDialogOpen}
              template={editingTemplate}
              companies={companies}
              onSave={handleSaveTemplate}
            />
            </div>
          </TabsContent>

          {/* Licenses Tab */}
          <TabsContent value="licenses" className="w-full">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-heading font-semibold">Licenses</h2>
                <Button onClick={() => setLicenseDialogOpen(true)} data-testid="add-license-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Generate License
                </Button>
              </div>

              <div className="grid gap-4">
              {licenses.map((license) => (
                <Card key={license.license_id} data-testid={`license-${license.license_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <code className="text-lg font-mono bg-muted px-3 py-1 rounded">{license.license_key}</code>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(license.license_key)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Badge variant={license.is_active ? "default" : "destructive"}>
                            {license.is_active ? "Active" : "Revoked"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            <Box className="h-3 w-3 mr-1" />
                            {license.app_name || "Unknown App"}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {companies.find(c => c.company_id === license.company_id)?.name || license.company_id}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span>Expires: {new Date(license.expiry_date).toLocaleDateString()}</span>
                          <span>Devices: {license.activated_devices.length}/{license.max_devices}</span>
                        </div>
                        {license.activated_devices.length > 0 && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium">Activated Devices:</p>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-orange-500 h-6 text-xs"
                                onClick={async () => {
                                  if (window.confirm("Clear all activated devices? Users will need to re-activate.")) {
                                    try {
                                      await axios.delete(`${API}/admin/licenses/${license.license_id}/devices`, { headers: getAuthHeaders() });
                                      toast.success("All devices cleared");
                                      fetchLicenses();
                                    } catch (error) {
                                      toast.error("Failed to clear devices");
                                    }
                                  }
                                }}
                              >
                                Clear All
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {license.activated_devices.map((device, idx) => (
                                <Badge key={idx} variant="outline" className="flex items-center gap-1">
                                  {device.device_name} ({device.device_id.slice(0, 8)}...)
                                  <button 
                                    className="ml-1 text-red-500 hover:text-red-700"
                                    onClick={async () => {
                                      try {
                                        await axios.delete(`${API}/admin/licenses/${license.license_id}/devices/${device.device_id}`, { headers: getAuthHeaders() });
                                        toast.success("Device removed");
                                        fetchLicenses();
                                      } catch (error) {
                                        toast.error("Failed to remove device");
                                      }
                                    }}
                                  >
                                    ×
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {license.is_active && (
                          <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleRevokeLicense(license.license_id)}>
                            Revoke
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* License Dialog */}
            <LicenseDialog 
              open={licenseDialogOpen} 
              onOpenChange={setLicenseDialogOpen}
              companies={companies}
              applications={applications}
              onCreate={handleCreateLicense}
            />
            </div>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="w-full">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-heading font-semibold">Processing Logs</h2>
                <Button onClick={exportLogs} variant="outline" data-testid="export-logs-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium">File</th>
                      <th className="text-left p-4 text-sm font-medium">Template</th>
                      <th className="text-left p-4 text-sm font-medium">Status</th>
                      <th className="text-left p-4 text-sm font-medium">Time</th>
                      <th className="text-left p-4 text-sm font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.log_id} className="border-b">
                        <td className="p-4">
                          <p className="text-sm font-medium truncate max-w-[200px]">{log.original_filename}</p>
                          {log.new_filename && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">→ {log.new_filename}</p>
                          )}
                        </td>
                        <td className="p-4 text-sm">{log.template_name || "—"}</td>
                        <td className="p-4">
                          <Badge variant={log.status === "success" ? "default" : log.status === "failed" ? "destructive" : "secondary"}>
                            {log.status}
                          </Badge>
                          {log.failure_reason && (
                            <p className="text-xs text-red-500 mt-1 truncate max-w-[150px]">{log.failure_reason}</p>
                          )}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {log.processing_time_ms ? `${log.processing_time_ms}ms` : "—"}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="w-full">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-heading font-semibold">Users</h2>
                <Button onClick={() => setUserDialogOpen(true)} data-testid="add-user-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </div>

              <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium">Name</th>
                      <th className="text-left p-4 text-sm font-medium">Email</th>
                      <th className="text-left p-4 text-sm font-medium">Role</th>
                      <th className="text-left p-4 text-sm font-medium">Company</th>
                      <th className="text-left p-4 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-b">
                        <td className="p-4 text-sm font-medium">{u.name}</td>
                        <td className="p-4 text-sm">{u.email}</td>
                        <td className="p-4">
                          <Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role}</Badge>
                        </td>
                        <td className="p-4 text-sm">
                          {companies.find(c => c.company_id === u.company_id)?.name || u.company_id || "—"}
                        </td>
                        <td className="p-4">
                          {u.user_id !== user.user_id && (
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={async () => {
                              if (window.confirm("Delete this user?")) {
                                try {
                                  await axios.delete(`${API}/admin/users/${u.user_id}`, { headers: getAuthHeaders() });
                                  fetchUsers();
                                  toast.success("User deleted");
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Failed to delete user");
                                }
                              }
                            }}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* User Dialog */}
            <UserDialog 
              open={userDialogOpen} 
              onOpenChange={setUserDialogOpen}
              companies={companies}
              onSave={async (data) => {
                await axios.post(`${API}/admin/users`, data, { headers: getAuthHeaders() });
                fetchUsers();
                setUserDialogOpen(false);
                toast.success("User created");
              }}
            />
            </div>
          </TabsContent>

          {/* Desktop Agent Tab */}
          <TabsContent value="agent" className="w-full">
            <div className="space-y-6">
              <h2 className="text-2xl font-heading font-semibold">Desktop Agent Management</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Current Status Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      Standalone EXE Status
                    </CardTitle>
                    <CardDescription>
                      {agentExeStatus?.github_configured 
                        ? "Auto-downloads from GitHub Releases" 
                        : "Manual upload or configure GitHub for auto-builds"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {agentExeStatus?.exists ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">Standalone EXE Available</span>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Size: {agentExeStatus.size_mb} MB</p>
                          <p>Source: {agentExeStatus.source === 'github' ? '🔄 GitHub CI/CD' : '📤 Manual Upload'}</p>
                          {agentExeStatus.version && <p>Version: {agentExeStatus.version}</p>}
                        </div>
                        <p className="text-sm text-green-600">
                          ✓ Customers will receive the standalone version
                        </p>
                        <div className="flex gap-2">
                          {agentExeStatus.github_configured && (
                            <Button variant="outline" size="sm" disabled={refreshingExe} data-testid="agent-exe-refresh-btn" onClick={async () => {
                              try {
                                setRefreshingExe(true);
                                await axios.post(`${API}/admin/agent-exe-refresh`, {}, { headers: getAuthHeaders() });
                                toast.info("Refresh started — fetching latest release from GitHub...");
                                // Poll the background task status (download can take a while)
                                const started = Date.now();
                                const poll = async () => {
                                  if (Date.now() - started > 5 * 60 * 1000) {
                                    toast.error("Refresh timed out. Check backend logs.");
                                    setRefreshingExe(false);
                                    return;
                                  }
                                  try {
                                    const res = await axios.get(`${API}/admin/agent-exe-refresh-status`, { headers: getAuthHeaders() });
                                    const st = res.data?.status;
                                    if (st === "completed") {
                                      toast.success(`Refreshed from GitHub${res.data.version ? ` (${res.data.version})` : ""}`);
                                      setRefreshingExe(false);
                                      fetchAgentExeStatus();
                                    } else if (st === "error") {
                                      toast.error(res.data.detail || "Failed to refresh");
                                      setRefreshingExe(false);
                                      fetchAgentExeStatus();
                                    } else {
                                      setTimeout(poll, 2000);
                                    }
                                  } catch (err) {
                                    setTimeout(poll, 3000);
                                  }
                                };
                                setTimeout(poll, 2000);
                              } catch (e) {
                                setRefreshingExe(false);
                                toast.error(e.response?.data?.detail || "Failed to refresh");
                              }
                            }}>
                              <RefreshCw className={`h-4 w-4 mr-2 ${refreshingExe ? "animate-spin" : ""}`} />
                              {refreshingExe ? "Refreshing..." : "Refresh"}
                            </Button>
                          )}
                          <Button variant="destructive" size="sm" onClick={handleDeleteExe}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="h-5 w-5" />
                          <span className="font-medium">No EXE Available</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {agentExeStatus?.github_configured 
                            ? "No releases found. Create a release on GitHub to auto-deploy."
                            : "Customers receive Python version. Configure GitHub CI/CD for auto-builds."}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Upload Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Upload className="h-5 w-5" />
                      Upload Standalone EXE
                    </CardTitle>
                    <CardDescription>
                      Build the exe once on Windows, then upload here
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept=".exe"
                        onChange={handleExeUpload}
                        className="hidden"
                        id="exe-upload"
                        disabled={uploadingExe}
                      />
                      <label htmlFor="exe-upload" className="cursor-pointer">
                        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium">
                          {uploadingExe ? "Uploading..." : "Click to upload DocuFlowAgent.exe"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Optional: Use if GitHub CI/CD not configured
                        </p>
                      </label>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* GitHub Connection Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    GitHub Connection
                  </CardTitle>
                  <CardDescription>
                    Token used to fetch agent builds from GitHub Releases. Update it here anytime — applies instantly, no redeploy needed.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p data-testid="github-config-repo">
                      Repo: <code className="bg-muted px-1 rounded">{githubConfig?.repo || "not configured"}</code>
                    </p>
                    <p data-testid="github-config-token-status">
                      Token:{" "}
                      {githubConfig?.token_set ? (
                        <span className="text-green-600">
                          Set ({githubConfig.token_hint}) — source: {githubConfig.token_source === "database" ? "saved via portal" : "environment variable"}
                        </span>
                      ) : (
                        <span className="text-amber-600">Not set</span>
                      )}
                    </p>
                    {githubConfig?.updated_at && (
                      <p>Last updated: {new Date(githubConfig.updated_at).toLocaleString()}</p>
                    )}
                  </div>
                  <div className="flex gap-2 max-w-xl">
                    <Input
                      type="password"
                      placeholder="Paste new GitHub token (github_pat_... or ghp_...)"
                      value={newGithubToken}
                      onChange={(e) => setNewGithubToken(e.target.value)}
                      data-testid="github-token-input"
                    />
                    <Button
                      size="sm"
                      className="shrink-0"
                      disabled={savingGithubToken || !newGithubToken.trim()}
                      onClick={handleSaveGithubToken}
                      data-testid="github-token-save-btn"
                    >
                      {savingGithubToken ? "Validating..." : "Save & Validate"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The token is validated against GitHub before saving. If you see "GitHub API returned 401" errors, paste a fresh token here.
                  </p>
                </CardContent>
              </Card>

              {/* Instructions Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Setup Options</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-950">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        Recommended: GitHub CI/CD (Automated)
                      </h4>
                      <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                        <li>Push code to GitHub with the workflow file</li>
                        <li>Set <code className="bg-muted px-1 rounded">GITHUB_REPO</code> env var</li>
                        <li>Create a release on GitHub</li>
                        <li>EXE auto-deploys to customers!</li>
                      </ol>
                      <p className="text-xs text-green-600 mt-2">
                        ✓ No manual steps after initial setup
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Alternative: Manual Upload</h4>
                      <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                        <li>Download agent from Client Dashboard</li>
                        <li>Run <code className="bg-muted px-1 rounded">build/BUILD_EXE.bat</code> on Windows</li>
                        <li>Upload exe using the upload card above</li>
                      </ol>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 border rounded-lg bg-muted/50">
                    <h4 className="font-medium mb-2">Customer Flow (Same for both options)</h4>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">1. Configure folders</span>
                      <span>→</span>
                      <span className="flex items-center gap-1">2. Download agent</span>
                      <span>→</span>
                      <span className="flex items-center gap-1">3. Run INSTALL.bat</span>
                      <span>→</span>
                      <span className="font-medium text-green-600">Done! (Auto-starts on boot)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <BrandedFooter />
    </div>
  );
}

// Company Dialog Component
function CompanyDialog({ open, onOpenChange, company, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    contact_email: "",
    contact_name: "",
    ocr_engine: "gemini",
    max_devices: 3,
    can_view_extracted_values: true,
    can_review_failed_items: true,
    page_limit: 1000
  });

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        contact_email: company.contact_email,
        contact_name: company.contact_name,
        ocr_engine: company.ocr_engine,
        max_devices: company.max_devices,
        can_view_extracted_values: company.can_view_extracted_values,
        can_review_failed_items: company.can_review_failed_items,
        page_limit: company.page_limit || 1000
      });
    } else {
      setFormData({
        name: "",
        contact_email: "",
        contact_name: "",
        ocr_engine: "gemini",
        max_devices: 3,
        can_view_extracted_values: true,
        can_review_failed_items: true,
        page_limit: 1000
      });
    }
  }, [company, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{company ? "Edit Company" : "Add Company"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Company Name</Label>
            <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
          </div>
          <div>
            <Label>Contact Email</Label>
            <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({...formData, contact_email: e.target.value})} />
          </div>
          <div>
            <Label>Contact Name</Label>
            <Input value={formData.contact_name} onChange={(e) => setFormData({...formData, contact_name: e.target.value})} />
          </div>
          <div>
            <Label>OCR Engine</Label>
            <Select value={formData.ocr_engine} onValueChange={(v) => setFormData({...formData, ocr_engine: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gemini">Gemini 2.5 Flash (Standard)</SelectItem>
                <SelectItem value="gemini-lite">Gemini 2.0 Flash-Lite (Budget)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Standard: Higher accuracy | Budget: ~3x cheaper, good accuracy
            </p>
          </div>
          <div>
            <Label>Max Devices</Label>
            <Input type="number" value={formData.max_devices} onChange={(e) => setFormData({...formData, max_devices: parseInt(e.target.value)})} />
          </div>
          <div>
            <Label>Page Limit (Quota)</Label>
            <Input 
              type="number" 
              value={formData.page_limit} 
              onChange={(e) => setFormData({...formData, page_limit: parseInt(e.target.value) || 1000})} 
              min="1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Maximum pages this company can process. Set -1 for unlimited.
            </p>
          </div>
          <div className="flex items-center justify-between">
            <Label>Can View Extracted Values</Label>
            <Switch checked={formData.can_view_extracted_values} onCheckedChange={(v) => setFormData({...formData, can_view_extracted_values: v})} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Can Review Failed Items</Label>
            <Switch checked={formData.can_review_failed_items} onCheckedChange={(v) => setFormData({...formData, can_review_failed_items: v})} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(formData)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Template Dialog Component with PDF Extraction
function TemplateDialog({ open, onOpenChange, template, companies, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    company_id: "",
    document_type: "general",
    filename_format: "",
    duplicate_handling: "append_number",
    fields: [],
    page_scan_mode: "first_only",
    page_scan_value: "",
    is_catalog_template: false,
    industry_tag: "",
    use_zone_ocr: false,
    sample_image_base64: "",
    missing_field_placeholder: ""
  });
  
  // Dialog tab state
  const [activeDialogTab, setActiveDialogTab] = useState("basics");
  
  // PDF extraction state
  const [extracting, setExtracting] = useState(false);
  const [extractedFields, setExtractedFields] = useState(null);
  const [selectedExtractedFields, setSelectedExtractedFields] = useState([]);
  
  // Zone marking state
  const [zoneMarkingMode, setZoneMarkingMode] = useState(false);
  const [activeFieldForZone, setActiveFieldForZone] = useState(null);
  const [sampleImageUrl, setSampleImageUrl] = useState(null);
  
  // Template catalog state
  const [catalogTemplates, setCatalogTemplates] = useState([]);
  const [industryTags, setIndustryTags] = useState([]);
  const [selectedCatalogTemplate, setSelectedCatalogTemplate] = useState("");
  
  // Auto-generate filename format when fields change
  const generateFilenameFormat = (fields) => {
    if (fields.length === 0) return "";
    const fieldPlaceholders = fields.slice(0, 4).map(f => `{${f.field_name}}`).filter(f => f !== "{}");
    return fieldPlaceholders.length > 0 ? fieldPlaceholders.join("_") + ".pdf" : "";
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch catalog templates and industry tags
  useEffect(() => {
    if (open && !template) {
      // Only fetch when creating new template
      axios.get(`${API}/admin/template-catalog`, { headers: getAuthHeaders() })
        .then(res => setCatalogTemplates(res.data.templates || []))
        .catch(() => {});
      axios.get(`${API}/admin/industry-tags`, { headers: getAuthHeaders() })
        .then(res => setIndustryTags(res.data.tags || []))
        .catch(() => {});
    }
  }, [open, template]);

  // Handle copying from catalog template
  const handleCopyFromCatalog = (templateId) => {
    const source = catalogTemplates.find(t => t.template_id === templateId);
    if (source) {
      setFormData(prev => ({
        ...prev,
        fields: source.fields || [],
        filename_format: source.filename_format || prev.filename_format,
        document_type: source.document_type || prev.document_type
      }));
      setSelectedCatalogTemplate(templateId);
      toast.success(`Copied fields from "${source.name}"`);
    }
  };

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        company_id: template.company_id || "",
        document_type: template.document_type || "general",
        filename_format: template.filename_format || "",
        duplicate_handling: template.duplicate_handling || "append_number",
        fields: template.fields || [],
        page_scan_mode: template.page_scan_mode || "first_only",
        page_scan_value: template.page_scan_value || "",
        is_catalog_template: template.is_catalog_template || false,
        industry_tag: template.industry_tag || "",
        use_zone_ocr: template.use_zone_ocr || false,
        sample_image_base64: template.sample_image_base64 || "",
        missing_field_placeholder: template.missing_field_placeholder ?? ""
      });
      setExtractedFields(null);
      setSelectedExtractedFields([]);
      setSelectedCatalogTemplate("");
      setActiveDialogTab("basics");
    } else {
      setFormData({
        name: "",
        company_id: companies[0]?.company_id || "",
        document_type: "general",
        filename_format: "",
        duplicate_handling: "append_number",
        fields: [],
        page_scan_mode: "first_only",
        page_scan_value: "",
        is_catalog_template: false,
        industry_tag: "",
        use_zone_ocr: false,
        sample_image_base64: "",
        missing_field_placeholder: ""
      });
      setExtractedFields(null);
      setSelectedExtractedFields([]);
      setSelectedCatalogTemplate("");
      setActiveDialogTab("basics");
    }
  }, [template, open, companies]);

  const handleFileUploadAndExtract = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setExtracting(true);
    setExtractedFields(null);
    
    try {
      // First upload the file
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const uploadResponse = await axios.post(`${API}/documents/upload`, formDataUpload, {
        headers: getAuthHeaders()
      });
      
      const documentId = uploadResponse.data.document_id;
      
      // Then process it with OCR
      const processResponse = await axios.post(`${API}/documents/${documentId}/process`, {}, {
        headers: getAuthHeaders()
      });
      
      if (processResponse.data.extracted_data) {
        setExtractedFields(processResponse.data.extracted_data);
        // Auto-select all fields
        const fieldKeys = Object.keys(processResponse.data.extracted_data).filter(k => k !== 'line_items');
        setSelectedExtractedFields(fieldKeys);
        toast.success("Fields extracted successfully!");
      } else {
        toast.error("No fields could be extracted from this document");
      }
    } catch (error) {
      console.error("Extraction error:", error);
      toast.error(error.response?.data?.detail || "Failed to extract fields from PDF");
    } finally {
      setExtracting(false);
    }
  };

  const toggleExtractedField = (fieldName) => {
    setSelectedExtractedFields(prev => 
      prev.includes(fieldName)
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  const useExtractedFields = () => {
    if (selectedExtractedFields.length === 0) {
      toast.error("Please select at least one field");
      return;
    }
    
    const newFields = selectedExtractedFields.map(fieldName => ({
      field_name: fieldName,
      display_name: fieldName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      extraction_type: "ai",
      required: ["invoice_number", "vendor_name", "total_amount"].includes(fieldName),
      confidence_threshold: 0.7
    }));
    
    // Auto-generate filename format from the new fields
    const newFilenameFormat = generateFilenameFormat(newFields);
    
    setFormData(prev => ({
      ...prev,
      fields: newFields,
      filename_format: newFilenameFormat
    }));
    
    toast.success("Fields added to template");
  };

  const addField = () => {
    const newFields = [...formData.fields, { field_name: "", display_name: "", extraction_type: "ai", required: false, confidence_threshold: 0.7 }];
    const newFilenameFormat = generateFilenameFormat(newFields);
    setFormData({
      ...formData,
      fields: newFields,
      filename_format: newFilenameFormat || formData.filename_format
    });
  };

  const removeField = (index) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    const newFilenameFormat = generateFilenameFormat(newFields);
    setFormData({
      ...formData,
      fields: newFields,
      filename_format: newFilenameFormat || formData.filename_format
    });
  };

  const updateField = (index, key, value) => {
    const newFields = [...formData.fields];
    newFields[index] = { ...newFields[index], [key]: value };
    
    // Auto-update filename format when field_name changes
    let newFilenameFormat = formData.filename_format;
    if (key === "field_name") {
      newFilenameFormat = generateFilenameFormat(newFields);
    }
    
    setFormData({ 
      ...formData, 
      fields: newFields,
      filename_format: newFilenameFormat || formData.filename_format
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? "Edit Template" : "Create Template"}</DialogTitle>
          <DialogDescription>
            {template ? "Update template settings" : "Set up your OCR template in a few simple steps"}
          </DialogDescription>
        </DialogHeader>
        
        {/* Tabbed Interface */}
        <Tabs value={activeDialogTab} onValueChange={setActiveDialogTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="basics" className="text-sm">
              <Settings className="h-4 w-4 mr-1.5" />
              Basics
            </TabsTrigger>
            <TabsTrigger value="fields" className="text-sm">
              <FileText className="h-4 w-4 mr-1.5" />
              Fields {formData.fields.length > 0 && <Badge variant="secondary" className="ml-1.5 h-5 px-1.5">{formData.fields.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="advanced" className="text-sm">
              <Target className="h-4 w-4 mr-1.5" />
              Advanced
            </TabsTrigger>
          </TabsList>
          
          {/* TAB 1: Basics */}
          <TabsContent value="basics" className="space-y-4 mt-0">
            {/* Copy from Catalog Section - Only show when creating new template */}
            {!template && catalogTemplates.length > 0 && (
              <div className="p-3 border rounded-lg bg-blue-50">
                <div className="flex items-center gap-2 mb-2">
                  <Copy className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-800 text-sm">Quick Start: Copy from Template Library</span>
                </div>
                <Select value={selectedCatalogTemplate} onValueChange={handleCopyFromCatalog}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select a template to copy from..." />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogTemplates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name} {t.industry_tag && <span className="text-muted-foreground">({t.industry_tag})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCatalogTemplate && (
                  <p className="text-xs text-green-600 mt-2">✓ Fields copied! You can modify them in the Fields tab.</p>
                )}
              </div>
            )}

            {/* Basic Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  placeholder="e.g., ABC Supplier Invoices"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label>Company *</Label>
                <Select value={formData.company_id} onValueChange={(v) => setFormData({...formData, company_id: v})}>
                  <SelectTrigger data-testid="template-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Document Type</Label>
                <Select value={formData.document_type} onValueChange={(v) => setFormData({...formData, document_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Document</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="supplier_invoice">Supplier Invoice</SelectItem>
                    <SelectItem value="credit_note">Credit Note</SelectItem>
                    <SelectItem value="purchase_order">Purchase Order</SelectItem>
                    <SelectItem value="receipt">Receipt</SelectItem>
                    <SelectItem value="inspection_report">Inspection Report</SelectItem>
                    <SelectItem value="certificate">Certificate</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duplicate Handling</Label>
                <Select value={formData.duplicate_handling} onValueChange={(v) => setFormData({...formData, duplicate_handling: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="append_number">Append Number (1), (2)</SelectItem>
                    <SelectItem value="unique_suffix">Unique Suffix</SelectItem>
                    <SelectItem value="overwrite">Overwrite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Filename Format - with auto-generation indicator */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Filename Format</Label>
                {formData.fields.length > 0 && (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Auto-generated
                  </Badge>
                )}
              </div>
              <Input 
                value={formData.filename_format} 
                onChange={(e) => setFormData({...formData, filename_format: e.target.value})} 
                placeholder="{vendor_name}_{invoice_number}_{invoice_date}.pdf"
                data-testid="filename-format-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Uses first 4 fields automatically. Available: {formData.fields.map(f => `{${f.field_name}}`).join(', ') || 'Add fields in Fields tab'}
              </p>
            </div>

            <div>
              <Label>If a field is missing, use</Label>
              <Input
                value={formData.missing_field_placeholder}
                onChange={(e) => setFormData({...formData, missing_field_placeholder: e.target.value})}
                placeholder="(leave blank to drop the field from the filename)"
                maxLength={32}
                data-testid="missing-field-placeholder-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used in the renamed filename whenever the OCR cannot find a field. Leave blank to remove the field entirely (extra underscores or dashes are cleaned up automatically). Examples: <code>-</code>, <code>N/A</code>, <code>MISSING</code>, <code>&nbsp;</code> (space).
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setActiveDialogTab("fields")}>
                Next: Configure Fields →
              </Button>
            </div>
          </TabsContent>
          
          {/* TAB 2: Fields */}
          <TabsContent value="fields" className="space-y-4 mt-0 overflow-x-hidden">
            {/* PDF Upload Section - Compact */}
            <div className="p-4 border-2 border-dashed rounded-lg bg-muted/30">
              <div className="flex items-center justify-between gap-4 flex-wrap sm:flex-nowrap">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-8 w-8 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium">Auto-detect Fields from Sample</p>
                    <p className="text-sm text-muted-foreground">Upload a sample PDF/image to extract fields automatically</p>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={handleFileUploadAndExtract}
                    className="hidden"
                    id="sample-pdf-input"
                    disabled={extracting}
                  />
                  <label htmlFor="sample-pdf-input">
                    <Button asChild disabled={extracting} variant="outline">
                      <span>
                        {extracting ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload PDF
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>

            {/* Extracted Fields Selection */}
            {extractedFields && (
              <div className="p-4 border rounded-lg bg-green-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-green-800">Extracted Fields</h4>
                    <p className="text-sm text-green-600">Select the fields you want to include</p>
                  </div>
                  <Button size="sm" onClick={useExtractedFields} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Use Selected
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                  {Object.entries(extractedFields).map(([key, value]) => {
                    if (key === 'line_items') return null;
                    const isSelected = selectedExtractedFields.includes(key);
                    return (
                      <div 
                        key={key}
                        className={`p-2 rounded border cursor-pointer transition-all ${
                          isSelected ? 'bg-green-100 border-green-500' : 'bg-white border-gray-200 hover:border-green-300'
                        }`}
                        onClick={() => toggleExtractedField(key)}
                      >
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            checked={isSelected} 
                            onChange={() => {}} 
                            className="accent-green-600"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</p>
                            <p className="text-xs text-muted-foreground truncate">{String(value).substring(0, 40)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Fields List with Smart Extract */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Extraction Fields ({formData.fields.length})</Label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      try {
                        toast.info("Analyzing document... This may take a moment.");
                        const formDataUpload = new FormData();
                        formDataUpload.append("file", file);
                        
                        const response = await axios.post(`${API}/documents/smart-extract`, formDataUpload, {
                          headers: getAuthHeaders()
                        });
                        
                        if (response.data.field_suggestions) {
                          const newFields = response.data.field_suggestions.map(f => ({
                            field_name: f.field_name,
                            display_name: f.display_name,
                            extraction_type: "ai",
                            validation_format: null,
                            required: false,
                            confidence_threshold: 0.7,
                            sample_value: f.sample_value
                          }));
                          
                          const allFields = [...formData.fields, ...newFields];
                          const newFilenameFormat = generateFilenameFormat(allFields);
                          
                          setFormData(prev => ({
                            ...prev,
                            fields: allFields,
                            filename_format: newFilenameFormat || prev.filename_format
                          }));
                          
                          toast.success(`Found ${newFields.length} fields!`);
                        }
                      } catch (error) {
                        toast.error("Failed to extract fields from document");
                      }
                      e.target.value = "";
                    }}
                    className="hidden"
                    id="smart-extract-upload"
                  />
                  <label htmlFor="smart-extract-upload">
                    <Button asChild variant="outline" size="sm" className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100">
                      <span>
                        <Upload className="h-4 w-4 mr-1" /> Smart Extract
                      </span>
                    </Button>
                  </label>
                  <Button type="button" variant="outline" size="sm" onClick={addField}>
                    <Plus className="h-4 w-4 mr-1" /> Add Manual
                  </Button>
                </div>
              </div>
              
              {formData.fields.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground border rounded-lg bg-muted/20">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No fields configured yet</p>
                  <p className="text-xs">Use "Smart Extract" to auto-detect or add fields manually</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto overflow-x-hidden border rounded-lg p-2">
                  {formData.fields.map((field, index) => (
                    <div key={index} className="p-2 bg-muted/30 rounded min-w-0">
                      <div className="flex items-center gap-2">
                        <Input 
                          className="flex-1 min-w-0 h-8 text-sm" 
                          placeholder="field_name" 
                          value={field.field_name} 
                          onChange={(e) => updateField(index, "field_name", e.target.value)} 
                        />
                        <Input 
                          className="flex-1 min-w-0 h-8 text-sm" 
                          placeholder="Display Name" 
                          value={field.display_name} 
                          onChange={(e) => updateField(index, "display_name", e.target.value)} 
                        />
                        <div className="flex items-center gap-1 shrink-0">
                          <Switch 
                            checked={field.required} 
                            onCheckedChange={(v) => updateField(index, "required", v)} 
                          />
                          <span className="text-xs">Req</span>
                        </div>
                        <Button type="button" variant="ghost" size="sm" className="shrink-0 h-8 w-8 p-0" onClick={() => removeField(index)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      {field.sample_value && (
                        <div className="mt-1 pl-1 flex items-center gap-1">
                          <span className="text-xs text-green-600 font-medium">Sample:</span>
                          <span className="text-xs text-muted-foreground bg-white px-2 py-0.5 rounded border truncate max-w-[400px]" title={field.sample_value}>
                            {field.sample_value}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {formData.fields.length > 0 && (
                <div className="flex justify-between items-center mt-2 gap-2">
                  <p className="text-xs text-muted-foreground truncate flex-1 min-w-0" title={formData.filename_format}>
                    Filename: {formData.filename_format || "Configure in Basics tab"}
                  </p>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500 text-xs shrink-0"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, fields: [], filename_format: "" }));
                    }}
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </div>
            
            <div className="flex justify-between pt-2">
              <Button variant="outline" size="sm" onClick={() => setActiveDialogTab("basics")}>
                ← Back to Basics
              </Button>
              <Button variant="outline" size="sm" onClick={() => setActiveDialogTab("advanced")}>
                Next: Advanced Settings →
              </Button>
            </div>
          </TabsContent>
          
          {/* TAB 3: Advanced */}
          <TabsContent value="advanced" className="space-y-4 mt-0">
            {/* Page Scanning Rules */}
            <div className="p-4 border rounded-lg bg-slate-50">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-[#E01626]" />
                <Label className="text-sm font-semibold">Page Scanning Rules</Label>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Control which pages are scanned for OCR extraction.
              </p>
              
              <div className="grid grid-cols-2 gap-2">
                <div 
                  className={`p-2 border rounded cursor-pointer transition-all ${formData.page_scan_mode === "first_only" ? "border-[#E01626] bg-red-50" : "hover:border-gray-400"}`}
                  onClick={() => setFormData({...formData, page_scan_mode: "first_only", page_scan_value: ""})}
                >
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={formData.page_scan_mode === "first_only"} onChange={() => {}} className="accent-[#E01626]" />
                    <span className="text-sm">First page only</span>
                  </div>
                </div>
                
                <div 
                  className={`p-2 border rounded cursor-pointer transition-all ${formData.page_scan_mode === "first_n" ? "border-[#E01626] bg-red-50" : "hover:border-gray-400"}`}
                  onClick={() => setFormData({...formData, page_scan_mode: "first_n", page_scan_value: formData.page_scan_value || "2"})}
                >
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={formData.page_scan_mode === "first_n"} onChange={() => {}} className="accent-[#E01626]" />
                    <span className="text-sm">First</span>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      className="w-14 h-6 text-sm"
                      value={formData.page_scan_mode === "first_n" ? formData.page_scan_value : ""}
                      onChange={(e) => setFormData({...formData, page_scan_mode: "first_n", page_scan_value: e.target.value})}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm">pages</span>
                  </div>
                </div>
                
                <div 
                  className={`p-2 border rounded cursor-pointer transition-all ${formData.page_scan_mode === "specific" ? "border-[#E01626] bg-red-50" : "hover:border-gray-400"}`}
                  onClick={() => setFormData({...formData, page_scan_mode: "specific", page_scan_value: formData.page_scan_value || "1,3"})}
                >
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={formData.page_scan_mode === "specific"} onChange={() => {}} className="accent-[#E01626]" />
                    <span className="text-sm">Specific:</span>
                    <Input
                      placeholder="1,3,5"
                      className="w-20 h-6 text-sm"
                      value={formData.page_scan_mode === "specific" ? formData.page_scan_value : ""}
                      onChange={(e) => setFormData({...formData, page_scan_mode: "specific", page_scan_value: e.target.value})}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                
                <div 
                  className={`p-2 border rounded cursor-pointer transition-all ${formData.page_scan_mode === "range" ? "border-[#E01626] bg-red-50" : "hover:border-gray-400"}`}
                  onClick={() => setFormData({...formData, page_scan_mode: "range", page_scan_value: formData.page_scan_value || "1-3"})}
                >
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={formData.page_scan_mode === "range"} onChange={() => {}} className="accent-[#E01626]" />
                    <span className="text-sm">Range:</span>
                    <Input
                      placeholder="1-3"
                      className="w-16 h-6 text-sm"
                      value={formData.page_scan_mode === "range" ? formData.page_scan_value : ""}
                      onChange={(e) => setFormData({...formData, page_scan_mode: "range", page_scan_value: e.target.value})}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Template Library */}
            <div className="p-4 border rounded-lg bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Copy className="h-4 w-4 text-blue-600" />
                  <Label className="text-sm font-semibold">Save to Template Library</Label>
                </div>
                <Switch 
                  checked={formData.is_catalog_template} 
                  onCheckedChange={(v) => setFormData({...formData, is_catalog_template: v})} 
                />
              </div>
              {formData.is_catalog_template && (
                <div className="mt-3">
                  <Select 
                    value={formData.industry_tag} 
                    onValueChange={(v) => setFormData({...formData, industry_tag: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry tag..." />
                    </SelectTrigger>
                    <SelectContent>
                      {industryTags.map(tag => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    This template will be available to copy when creating templates for other companies.
                  </p>
                </div>
              )}
            </div>
            
            {/* Zone OCR */}
            <div className="p-4 border rounded-lg bg-amber-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-amber-600" />
                  <Label className="text-sm font-semibold">Zone OCR (Precision Extraction)</Label>
                  <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">Advanced</Badge>
                </div>
                <Switch 
                  checked={formData.use_zone_ocr} 
                  onCheckedChange={(v) => setFormData({...formData, use_zone_ocr: v})} 
                />
              </div>
              
              <p className="text-xs text-amber-800 mb-3">
                Extract data from specific regions. Best for fixed-format documents.
              </p>
              
              {formData.use_zone_ocr && (
                <div className="space-y-3">
                  {!formData.sample_image_base64 ? (
                    <div className="p-3 border-2 border-dashed border-amber-300 rounded-lg bg-white text-center">
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          try {
                            if (file.type === "application/pdf") {
                              const formDataUpload = new FormData();
                              formDataUpload.append("file", file);
                              
                              const response = await axios.post(`${API}/documents/preview`, formDataUpload, {
                                headers: getAuthHeaders()
                              });
                              
                              if (response.data.image_base64) {
                                setFormData(prev => ({
                                  ...prev,
                                  sample_image_base64: response.data.image_base64
                                }));
                                toast.success("Sample loaded. Now mark zones for each field.");
                              }
                            } else {
                              const reader = new FileReader();
                              reader.onload = () => {
                                const base64 = reader.result.split(",")[1];
                                setFormData(prev => ({
                                  ...prev,
                                  sample_image_base64: base64
                                }));
                                toast.success("Sample loaded. Now mark zones for each field.");
                              };
                              reader.readAsDataURL(file);
                            }
                          } catch (error) {
                            toast.error("Failed to load sample document");
                          }
                        }}
                        className="hidden"
                        id="zone-sample-upload"
                      />
                      <label htmlFor="zone-sample-upload">
                        <Button asChild variant="outline" size="sm" className="border-amber-400 text-amber-700 hover:bg-amber-100">
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Sample for Zone Marking
                          </span>
                        </Button>
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium">Mark extraction zones:</span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 h-6 text-xs"
                          onClick={() => setFormData(prev => ({ ...prev, sample_image_base64: "" }))}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                      
                      {formData.fields.length === 0 ? (
                        <div className="p-3 bg-white rounded border text-center text-muted-foreground text-xs">
                          Add fields in the Fields tab first, then mark their zones here.
                        </div>
                      ) : (
                        <ZoneMarker
                          imageBase64={formData.sample_image_base64}
                          fields={formData.fields}
                          onZonesUpdate={(zonesMap) => {
                            const updatedFields = formData.fields.map((field, index) => {
                              if (zonesMap[index]) {
                                return { ...field, position_zone: zonesMap[index] };
                              }
                              return field;
                            });
                            setFormData(prev => ({ ...prev, fields: updatedFields }));
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex justify-start pt-2">
              <Button variant="outline" size="sm" onClick={() => setActiveDialogTab("fields")}>
                ← Back to Fields
              </Button>
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              // Validate required fields before saving
              if (!formData.name?.trim()) {
                toast.error("Template name is required");
                setActiveDialogTab("basics");
                return;
              }
              if (!formData.company_id) {
                toast.error("Please select a company");
                setActiveDialogTab("basics");
                return;
              }
              if (!formData.fields || formData.fields.length === 0) {
                toast.error("Please add at least one extraction field");
                setActiveDialogTab("fields");
                return;
              }
              // Check if any field has empty field_name
              const emptyFields = formData.fields.filter(f => !f.field_name?.trim());
              if (emptyFields.length > 0) {
                toast.error("All fields must have a field name");
                setActiveDialogTab("fields");
                return;
              }
              if (!formData.filename_format?.trim()) {
                toast.error("Filename format is required");
                setActiveDialogTab("basics");
                return;
              }
              onSave(formData);
            }} 
            disabled={!formData.name || !formData.company_id}
            data-testid="save-template-btn"
          >
            {template ? "Update Template" : "Create Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// License Dialog Component
function LicenseDialog({ open, onOpenChange, companies, applications, onCreate }) {
  const [companyId, setCompanyId] = useState("");
  const [appId, setAppId] = useState("");
  const [expiryDays, setExpiryDays] = useState(365);
  const [maxDevices, setMaxDevices] = useState(3);

  // Set default app to first active app
  useEffect(() => {
    if (applications?.length > 0 && !appId) {
      const activeApp = applications.find(a => a.status === "active");
      if (activeApp) setAppId(activeApp.app_id);
    }
  }, [applications, appId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate License</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Application</Label>
            <Select value={appId} onValueChange={setAppId}>
              <SelectTrigger><SelectValue placeholder="Select application" /></SelectTrigger>
              <SelectContent>
                {applications?.filter(a => a.status === "active").map((a) => (
                  <SelectItem key={a.app_id} value={a.app_id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Company</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Expiry (days)</Label>
            <Input type="number" value={expiryDays} onChange={(e) => setExpiryDays(parseInt(e.target.value))} />
          </div>
          <div>
            <Label>Max Devices / Users</Label>
            <Input type="number" value={maxDevices} onChange={(e) => setMaxDevices(parseInt(e.target.value))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onCreate(companyId, appId, expiryDays, maxDevices)} disabled={!companyId || !appId}>Generate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// User Dialog Component
function UserDialog({ open, onOpenChange, companies, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "client",
    company_id: ""
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add User</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.role === "client" && (
            <div>
              <Label>Company</Label>
              <Select value={formData.company_id} onValueChange={(v) => setFormData({...formData, company_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(formData)}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
